import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Save, Eye, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Form, FormField, UpdateFormDTO } from '../../types/form';
import { useFormActions } from '../../hooks/form/useFormActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import FormBuilder from '../../components/forms/FormBuilder';
import { FormPreviewDialog } from '../../components/forms';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { ROUTES } from '../../config/constants/routes';

const STEPS = ['Basic Info', 'Form Fields', 'Messages'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FormEditView: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const { updateForm, getForm, isLoading } = useFormActions();
  const userEmail = useSelector((state: RootState) => state.auth.user?.email);

  // Loading state for initial fetch
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Store original formId (different from uuid used in URL)
  const [originalFormId, setOriginalFormId] = useState<string>('');

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Form state - Step 1
  const [formName, setFormName] = useState('');
  const [triggerPrompt, setTriggerPrompt] = useState('');
  const [description, setDescription] = useState('');

  // Form state - Step 2
  const [fields, setFields] = useState<FormField[]>([]);

  // Form state - Step 3
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [sendEmailCopy, setSendEmailCopy] = useState(false);
  const [emailIds, setEmailIds] = useState<string[]>(['']);

  // UI state
  const [error, setError] = useState('');
  const [flowError, setFlowError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // Fetch form data on mount
  useEffect(() => {
    const fetchFormData = async () => {
      if (!formId) {
        setFetchError('Form ID is required');
        setIsFetching(false);
        return;
      }

      try {
        const formData = await getForm(formId);
        // Store the original formId for use in update payload
        setOriginalFormId(formData.formId || '');
        setFormName(formData.formName || '');
        setTriggerPrompt(formData.triggerPrompt || '');
        setDescription(formData.description || '');
        let loadedFields = formData.fields || [];
        // Migration: if requiresVerification=true but no customer_verify field, inject one at order 0
        if (
          formData.requiresVerification &&
          !loadedFields.some((f: FormField) => f.fieldType === 'customer_verify')
        ) {
          const verifyField: FormField = {
            fieldId: `verify_customer_${Date.now()}`,
            label: 'Verify Customer',
            fieldType: 'customer_verify',
            required: false,
            verificationMethod: 'email',
            order: 0,
          };
          loadedFields = [
            verifyField,
            ...loadedFields.map((f: FormField, idx: number) => ({ ...f, order: idx + 1 })),
          ];
        }
        setFields(loadedFields);
        setSuccessMessage(formData.successMessage || 'Thank you for your submission!');
        setErrorMessage(
          formData.errorMessage || "Sorry, we couldn't submit your form. Please try again later."
        );
        setSendEmailCopy(formData.sendEmailCopy ?? false);
        setEmailIds(
          formData.emailIds && formData.emailIds.length > 0 ? formData.emailIds : [userEmail || '']
        );
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to fetch form');
      } finally {
        setIsFetching(false);
      }
    };

    fetchFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]); // Only depend on formId, getForm is stable from hook

  const validateStep = (step: number): boolean => {
    setError('');

    switch (step) {
      case 0:
        if (!formName.trim()) {
          setError('Form name is required');
          return false;
        }
        if (!triggerPrompt.trim()) {
          setError('Trigger prompt is required');
          return false;
        }
        return true;

      case 1:
        if (fields.length === 0) {
          setError('Please add at least one field to the form');
          return false;
        }
        if (flowError) {
          setError(flowError);
          return false;
        }
        return true;

      case 2:
        if (!successMessage.trim()) {
          setError('Success message is required');
          return false;
        }
        if (!errorMessage.trim()) {
          setError('Error message is required');
          return false;
        }
        if (sendEmailCopy) {
          const emails = emailIds.map(e => e.trim()).filter(e => e !== '');
          if (emails.length === 0) {
            setError('Please add at least one email address or uncheck "Send a copy to an Email?"');
            return false;
          }
          if (emails.some(e => !EMAIL_REGEX.test(e))) {
            setError('Please fix the invalid email address(es)');
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  const handleEmailChange = (index: number, value: string) => {
    setEmailIds(prev => prev.map((email, i) => (i === index ? value : email)));
  };

  const handleAddEmail = () => {
    setEmailIds(prev => [...prev, '']);
  };

  const handleRemoveEmail = (index: number) => {
    setEmailIds(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSave = async () => {
    if (!validateStep(activeStep) || !formId) return;

    try {
      const formData: UpdateFormDTO = {
        formName: formName.trim(),
        triggerPrompt: triggerPrompt.trim(),
        description: description.trim(),
        requiresVerification: fields.some(f => f.fieldType === 'customer_verify'),
        fields: fields.map((f, idx) => ({ ...f, order: idx + 1 })),
        successMessage: successMessage.trim(),
        errorMessage: errorMessage.trim(),
        sendEmailCopy,
        emailIds: sendEmailCopy ? emailIds.map(e => e.trim()).filter(e => e !== '') : [],
      };

      // formId from URL is actually uuid, pass originalFormId for the formValue payload
      await updateForm(formId, formData, originalFormId);
      setNotification({
        open: true,
        message: 'Form updated successfully',
        severity: SEVERITY.SUCCESS,
      });

      // Navigate back to forms list after short delay
      setTimeout(() => {
        navigate(ROUTES.FORMS);
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update form');
    }
  };

  const getPreviewForm = (): Form => ({
    formId: formId || '',
    clientId: '',
    formName,
    triggerPrompt,
    description,
    requiresVerification: fields.some(f => f.fieldType === 'customer_verify'),
    fields,
    saveLocal: true,
    successMessage,
    errorMessage,
    isActive: true,
  });

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(ROUTES.FORMS)}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Navigation Icons and Step Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {/* Left: Back and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to Forms">
            <IconButton onClick={() => navigate(ROUTES.FORMS)} size="small">
              <ArrowLeft size={20} />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Edit Form
          </Typography>
        </Box>

        {/* Center: Step Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {STEPS.map((label, index) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor:
                    activeStep === index
                      ? theme.palette.primary.main
                      : activeStep > index
                        ? theme.palette.success.main
                        : theme.palette.action.disabledBackground,
                  color: activeStep >= index ? '#fff' : theme.palette.text.secondary,
                }}
              >
                {index + 1}
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    width: 24,
                    height: 2,
                    backgroundColor:
                      activeStep > index
                        ? theme.palette.success.main
                        : theme.palette.action.disabledBackground,
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Right: Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Eye size={18} />}
            onClick={() => setPreviewOpen(true)}
            disabled={fields.length === 0}
          >
            Preview
          </Button>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChevronLeft size={18} />}
              onClick={handleBack}
            >
              Back
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              size="small"
              endIcon={<ChevronRight size={18} />}
              onClick={handleNext}
              disableElevation
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={
                isLoading ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />
              }
              onClick={handleSave}
              disabled={isLoading}
              disableElevation
            >
              {isLoading ? 'Saving...' : 'Update Form'}
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={() => navigate(ROUTES.FORMS)}
            sx={{ color: theme.palette.text.secondary }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Paper
        elevation={0}
        sx={{
          p: activeStep === 1 ? 2 : 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Basic Information */}
        {activeStep === 0 && (
          <Box sx={{ maxWidth: 600 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Basic Information
            </Typography>

            <TextField
              label="Form Name"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
              placeholder="e.g., Customer Feedback"
              helperText="A descriptive name for your form"
            />

            <TextField
              label="Trigger Prompt"
              value={triggerPrompt}
              onChange={e => setTriggerPrompt(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              sx={{ mb: 3 }}
              placeholder="e.g., feedback, complaint, share experience, review, suggestion"
              helperText="Keywords or phrases that will trigger this form in AI conversations. Separate with commas."
            />

            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 3 }}
              placeholder="e.g., Collect customer feedback about their experience"
              helperText="Internal description for reference (not shown to users)"
            />
          </Box>
        )}

        {/* Step 2: Form Builder */}
        {activeStep === 1 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
              sx={{
                flex: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                minHeight: 400,
              }}
            >
              <FormBuilder fields={fields} onChange={setFields} onFlowError={setFlowError} />
            </Box>
          </Box>
        )}

        {/* Step 3: Messages */}
        {activeStep === 2 && (
          <Box sx={{ maxWidth: 600 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Form Messages - captured
            </Typography>

            <TextField
              label="Success Message"
              value={successMessage}
              onChange={e => setSuccessMessage(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              sx={{ mb: 3 }}
              placeholder="e.g., Thank you for your feedback! We appreciate you taking the time to share your experience with us."
              helperText="Message shown when form is submitted successfully"
            />

            <TextField
              label="Error Message"
              value={errorMessage}
              onChange={e => setErrorMessage(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              sx={{ mb: 3 }}
              placeholder="e.g., Sorry, we couldn't submit your feedback. Please try again later."
              helperText="Message shown when form submission fails"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmailCopy}
                  onChange={e => setSendEmailCopy(e.target.checked)}
                />
              }
              label="Send a copy to an Email?"
              sx={{ mb: sendEmailCopy ? 1 : 0 }}
            />

            {sendEmailCopy && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  These email addresses will receive a copy of every form submission
                </Typography>
                {emailIds.map((email, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
                  >
                    <TextField
                      value={email}
                      onChange={e => handleEmailChange(index, e.target.value)}
                      size="small"
                      fullWidth
                      type="email"
                      placeholder="e.g., manager@company.com"
                      error={email.trim() !== '' && !EMAIL_REGEX.test(email.trim())}
                      helperText={
                        email.trim() !== '' && !EMAIL_REGEX.test(email.trim())
                          ? 'Invalid email address'
                          : ''
                      }
                    />
                    <IconButton
                      onClick={() => handleRemoveEmail(index)}
                      size="small"
                      color="error"
                      disabled={emailIds.length === 1}
                      sx={{ mt: 0.5 }}
                      aria-label="Remove email"
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={handleAddEmail}
                  sx={{ mt: 0.5 }}
                >
                  Add Email
                </Button>
              </Box>
            )}

            {/* Summary */}
            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: `${theme.palette.primary.main}08`,
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Form Summary
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {formName}
              </Typography>
              <Typography variant="body2">
                <strong>Fields:</strong> {fields.length}
              </Typography>
              <Typography variant="body2">
                <strong>Triggers:</strong> {triggerPrompt.split(',').filter(k => k.trim()).length}{' '}
                keyword(s)
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Preview Dialog */}
      <FormPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        form={getPreviewForm()}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default FormEditView;
